import { Users, Hash, Mail } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";

import { usePersonProfile, MAX_FUNCOES_VISIVEIS } from "./use-person-profile";
import { ProfileHeader } from "./ProfileHeader";
import { CollapsibleChips } from "./CollapsibleChips";
import { ProfileChip } from "./ProfileChip";
import { CopyableInfoRow } from "./CopyableInfoRow";
import { AdminSecurityPanel } from "./AdminSecurityPanel";
import { ProfileFooter } from "./ProfileFooter";

interface PersonProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pessoaId: number | null;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <Users className="h-3.5 w-3.5" />
      {children}
    </div>
  );
}

export function PersonProfileDialog({ open, onOpenChange, pessoaId }: PersonProfileDialogProps) {
  const profile = usePersonProfile(pessoaId);
  const { copiedKey, copy } = useCopyToClipboard();

  if (profile.isLoading || !profile.pessoa) return null;

  const {
    pessoa,
    status,
    isExColaborador,
    isAusente,
    showAdminSecurity,
    funcoesOrdenadas,
    squadsOrdenados,
    membroDesde,
    ultimoAcessoLabel,
    isLastLoginOverdue,
  } = profile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto border border-border/50 bg-background p-0 shadow-2xl sm:w-full">
        <ProfileHeader nome={pessoa.nome} status={status} foto={pessoa.foto || pessoa.fotoThumb} />

        <div className="relative px-4 pb-6 sm:px-6">
          {/* Organização (Funções e Verticais) */}
          <div className="space-y-5 rounded-xl border border-border/40 bg-secondary/30 p-4">
            <div className="space-y-2.5">
              <SectionLabel>Cargos</SectionLabel>
              <CollapsibleChips
                items={funcoesOrdenadas}
                maxVisible={MAX_FUNCOES_VISIVEIS}
                emptyLabel="Nenhum cargo atribuído"
              />
            </div>

            <div className="space-y-2.5">
              <SectionLabel>Verticais</SectionLabel>
              <div className="flex flex-wrap gap-2">
                {squadsOrdenados.length > 0 ? (
                  squadsOrdenados.map((squad) => (
                    <ProfileChip key={squad.id} variant="neutral">
                      {squad.nome}
                    </ProfileChip>
                  ))
                ) : (
                  <span className="text-sm italic text-muted-foreground">Nenhum vínculo</span>
                )}
              </div>
            </div>

            {showAdminSecurity && (
              <>
                <Separator />
                <AdminSecurityPanel
                  abrangenciaNivel={pessoa.abrangenciaNivel}
                  sensibilidadeNivel={pessoa.sensibilidadeNivel}
                />
              </>
            )}
          </div>

          {/* Contato */}
          <div className="mt-5 flex flex-col gap-y-2">
            <CopyableInfoRow
              icon={Hash}
              label="ID no Sistema"
              value={String(pessoa.id)}
              isCopied={copiedKey === "id"}
              copyTooltip="Copiar ID"
              onCopy={() => copy(pessoa.id, { key: "id", successMessage: "ID copiado" })}
            />

            <CopyableInfoRow
              icon={Mail}
              label="E-mail Institucional"
              value={pessoa.email || "Não informado"}
              isCopied={copiedKey === "email"}
              copyTooltip="Copiar e-mail"
              onCopy={
                pessoa.email
                  ? () => copy(pessoa.email, { key: "email", successMessage: "E-mail copiado" })
                  : undefined
              }
            />
          </div>

          <ProfileFooter
            membroDesde={membroDesde}
            ultimoAcessoLabel={ultimoAcessoLabel}
            isLastLoginOverdue={isLastLoginOverdue}
            isExColaborador={isExColaborador}
            isAusente={isAusente}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
